import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  User,
  Building2,
  Landmark,
  Plus,
  Search,
  Star,
  StarOff,
  Trash2,
  Edit2,
  X,
  Check,
} from 'lucide-react'
import { cn } from '@/utils'

// Helper to get auth token
const getAuthToken = () => localStorage.getItem('niki_access_token')

interface Contact {
  id: string
  nickname: string
  display_name?: string
  contact_type: 'person' | 'merchant' | 'bank'
  phone?: string
  paybill?: string
  till_number?: string
  account_number?: string
  bank_name?: string
  bank_account?: string
  email?: string
  notes?: string
  aliases?: string
  is_favorite: boolean
  use_count: number
}

const contactTypeIcons = {
  person: User,
  merchant: Building2,
  bank: Landmark,
}

const contactTypeLabels = {
  person: 'Person',
  merchant: 'Business',
  bank: 'Bank',
}

export function ContactsPage() {
  const [contacts, setContacts] = useState<Contact[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [filter, setFilter] = useState<'all' | 'person' | 'merchant' | 'bank'>('all')
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingContact, setEditingContact] = useState<Contact | null>(null)

  useEffect(() => {
    fetchContacts()
  }, [filter])

  const fetchContacts = async () => {
    try {
      const params = new URLSearchParams()
      if (filter !== 'all') params.set('contact_type', filter)
      if (searchQuery) params.set('search', searchQuery)
      
      const res = await fetch(`/api/v1/contacts?${params}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      })
      if (res.ok) {
        const data = await res.json()
        setContacts(data)
      }
    } catch (error) {
      console.error('Failed to fetch contacts:', error)
    } finally {
      setLoading(false)
    }
  }

  const toggleFavorite = async (contactId: string) => {
    try {
      const res = await fetch(`/api/v1/contacts/${contactId}/favorite`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      })
      if (res.ok) {
        setContacts(contacts.map(c => 
          c.id === contactId ? { ...c, is_favorite: !c.is_favorite } : c
        ))
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error)
    }
  }

  const deleteContact = async (contactId: string) => {
    if (!confirm('Delete this contact?')) return
    
    try {
      const res = await fetch(`/api/v1/contacts/${contactId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      })
      if (res.ok) {
        setContacts(contacts.filter(c => c.id !== contactId))
      }
    } catch (error) {
      console.error('Failed to delete contact:', error)
    }
  }

  const filteredContacts = contacts.filter(c => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return (
      c.nickname.toLowerCase().includes(query) ||
      c.display_name?.toLowerCase().includes(query) ||
      c.phone?.includes(query)
    )
  })

  const favoriteContacts = filteredContacts.filter(c => c.is_favorite)
  const otherContacts = filteredContacts.filter(c => !c.is_favorite)

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Contacts</h1>
          <p className="text-white/50 text-sm">People and businesses you send to</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn-primary flex items-center gap-2 px-4 py-2"
        >
          <Plus className="w-4 h-4" />
          Add
        </button>
      </div>

      {/* Search & Filter */}
      <div className="glass-card p-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input
              type="text"
              placeholder="Search contacts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-2 text-white placeholder-white/40 focus:outline-none focus:border-primary/50"
            />
          </div>
          <div className="flex gap-1">
            {(['all', 'person', 'merchant', 'bank'] as const).map((type) => (
              <button
                key={type}
                onClick={() => setFilter(type)}
                className={cn(
                  'px-3 py-2 rounded-lg text-sm transition-colors',
                  filter === type
                    ? 'bg-primary/20 text-primary-light'
                    : 'text-white/60 hover:bg-white/5'
                )}
              >
                {type === 'all' ? 'All' : contactTypeLabels[type]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Contacts List */}
      {loading ? (
        <div className="text-center py-12 text-white/50">Loading...</div>
      ) : filteredContacts.length === 0 ? (
        <div className="text-center py-12">
          <User className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <p className="text-white/50">No contacts yet</p>
          <p className="text-white/30 text-sm">Add contacts like "Mom", "Dad", or businesses</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Favorites */}
          {favoriteContacts.length > 0 && (
            <div>
              <h2 className="text-sm font-medium text-white/50 mb-3 flex items-center gap-2">
                <Star className="w-4 h-4" />
                Favorites
              </h2>
              <div className="space-y-2">
                {favoriteContacts.map((contact) => (
                  <ContactCard
                    key={contact.id}
                    contact={contact}
                    onToggleFavorite={toggleFavorite}
                    onEdit={setEditingContact}
                    onDelete={deleteContact}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Other Contacts */}
          {otherContacts.length > 0 && (
            <div>
              {favoriteContacts.length > 0 && (
                <h2 className="text-sm font-medium text-white/50 mb-3">All Contacts</h2>
              )}
              <div className="space-y-2">
                {otherContacts.map((contact) => (
                  <ContactCard
                    key={contact.id}
                    contact={contact}
                    onToggleFavorite={toggleFavorite}
                    onEdit={setEditingContact}
                    onDelete={deleteContact}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {(showAddModal || editingContact) && (
          <ContactModal
            contact={editingContact}
            onClose={() => {
              setShowAddModal(false)
              setEditingContact(null)
            }}
            onSave={() => {
              setShowAddModal(false)
              setEditingContact(null)
              fetchContacts()
            }}
          />
        )}
      </AnimatePresence>
    </div>
  )
}

// Contact Card Component
function ContactCard({
  contact,
  onToggleFavorite,
  onEdit,
  onDelete,
}: {
  contact: Contact
  onToggleFavorite: (id: string) => void
  onEdit: (contact: Contact) => void
  onDelete: (id: string) => void
}) {
  const Icon = contactTypeIcons[contact.contact_type]
  
  const getSubtitle = () => {
    if (contact.phone) return contact.phone
    if (contact.paybill) return `Paybill: ${contact.paybill}`
    if (contact.till_number) return `Till: ${contact.till_number}`
    if (contact.bank_account) return `${contact.bank_name}: ${contact.bank_account}`
    return contact.email || ''
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card p-4 flex items-center gap-4 group"
    >
      <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
        <Icon className="w-6 h-6 text-primary" />
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-white truncate">{contact.nickname}</h3>
          {contact.display_name && contact.display_name !== contact.nickname && (
            <span className="text-white/40 text-sm truncate">({contact.display_name})</span>
          )}
        </div>
        <p className="text-white/50 text-sm truncate">{getSubtitle()}</p>
      </div>

      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => onToggleFavorite(contact.id)}
          className="p-2 rounded-lg hover:bg-white/10 transition-colors"
        >
          {contact.is_favorite ? (
            <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
          ) : (
            <StarOff className="w-4 h-4 text-white/40" />
          )}
        </button>
        <button
          onClick={() => onEdit(contact)}
          className="p-2 rounded-lg hover:bg-white/10 transition-colors"
        >
          <Edit2 className="w-4 h-4 text-white/40" />
        </button>
        <button
          onClick={() => onDelete(contact.id)}
          className="p-2 rounded-lg hover:bg-red-500/20 transition-colors"
        >
          <Trash2 className="w-4 h-4 text-red-400" />
        </button>
      </div>
    </motion.div>
  )
}

// Add/Edit Modal Component
function ContactModal({
  contact,
  onClose,
  onSave,
}: {
  contact: Contact | null
  onClose: () => void
  onSave: () => void
}) {
  
  const [form, setForm] = useState({
    nickname: contact?.nickname || '',
    display_name: contact?.display_name || '',
    contact_type: contact?.contact_type || 'person',
    phone: contact?.phone || '',
    paybill: contact?.paybill || '',
    till_number: contact?.till_number || '',
    account_number: contact?.account_number || '',
    bank_name: contact?.bank_name || '',
    bank_account: contact?.bank_account || '',
    email: contact?.email || '',
    notes: contact?.notes || '',
    aliases: contact?.aliases || '',
    is_favorite: contact?.is_favorite || false,
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const handleSave = async () => {
    if (!form.nickname.trim()) {
      setError('Nickname is required')
      return
    }

    setSaving(true)
    setError('')

    try {
      const url = contact
        ? `/api/v1/contacts/${contact.id}`
        : '/api/v1/contacts'
      
      const res = await fetch(url, {
        method: contact ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify(form),
      })

      if (res.ok) {
        onSave()
      } else {
        const data = await res.json()
        setError(data.detail || 'Failed to save contact')
      }
    } catch (err) {
      setError('Failed to save contact')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="fixed inset-4 z-50 flex items-center justify-center pointer-events-none"
      >
        <div className="glass-card w-full max-w-md max-h-[90vh] overflow-y-auto pointer-events-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <h2 className="text-xl font-semibold text-white">
              {contact ? 'Edit Contact' : 'Add Contact'}
            </h2>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10">
              <X className="w-5 h-5 text-white/60" />
            </button>
          </div>

          {/* Form */}
          <div className="p-6 space-y-4">
            {error && (
              <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm">
                {error}
              </div>
            )}

            {/* Nickname */}
            <div>
              <label className="text-sm text-white/70 mb-1 block">Nickname *</label>
              <input
                type="text"
                placeholder="e.g., Mom, Dad, Safaricom"
                value={form.nickname}
                onChange={(e) => setForm({ ...form, nickname: e.target.value })}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white placeholder-white/40 focus:outline-none focus:border-primary/50"
              />
            </div>

            {/* Display Name */}
            <div>
              <label className="text-sm text-white/70 mb-1 block">Full Name</label>
              <input
                type="text"
                placeholder="e.g., Jane Doe"
                value={form.display_name}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white placeholder-white/40 focus:outline-none focus:border-primary/50"
              />
            </div>

            {/* Type */}
            <div>
              <label className="text-sm text-white/70 mb-1 block">Type</label>
              <div className="flex gap-2">
                {(['person', 'merchant', 'bank'] as const).map((type) => {
                  const Icon = contactTypeIcons[type]
                  return (
                    <button
                      key={type}
                      onClick={() => setForm({ ...form, contact_type: type })}
                      className={cn(
                        'flex-1 p-3 rounded-lg border flex flex-col items-center gap-1 transition-colors',
                        form.contact_type === type
                          ? 'border-primary bg-primary/10'
                          : 'border-white/10 hover:border-white/20'
                      )}
                    >
                      <Icon className={cn(
                        'w-5 h-5',
                        form.contact_type === type ? 'text-primary' : 'text-white/60'
                      )} />
                      <span className={cn(
                        'text-xs',
                        form.contact_type === type ? 'text-white' : 'text-white/60'
                      )}>
                        {contactTypeLabels[type]}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Conditional fields based on type */}
            {form.contact_type === 'person' && (
              <div>
                <label className="text-sm text-white/70 mb-1 block">Phone Number</label>
                <input
                  type="tel"
                  placeholder="e.g., 0722123456"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white placeholder-white/40 focus:outline-none focus:border-primary/50"
                />
              </div>
            )}

            {form.contact_type === 'merchant' && (
              <>
                <div>
                  <label className="text-sm text-white/70 mb-1 block">Paybill Number</label>
                  <input
                    type="text"
                    placeholder="e.g., 174379"
                    value={form.paybill}
                    onChange={(e) => setForm({ ...form, paybill: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white placeholder-white/40 focus:outline-none focus:border-primary/50"
                  />
                </div>
                <div>
                  <label className="text-sm text-white/70 mb-1 block">Account Number</label>
                  <input
                    type="text"
                    placeholder="Account number (if required)"
                    value={form.account_number}
                    onChange={(e) => setForm({ ...form, account_number: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white placeholder-white/40 focus:outline-none focus:border-primary/50"
                  />
                </div>
              </>
            )}

            {form.contact_type === 'bank' && (
              <>
                <div>
                  <label className="text-sm text-white/70 mb-1 block">Bank Name</label>
                  <input
                    type="text"
                    placeholder="e.g., KCB, Equity"
                    value={form.bank_name}
                    onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white placeholder-white/40 focus:outline-none focus:border-primary/50"
                  />
                </div>
                <div>
                  <label className="text-sm text-white/70 mb-1 block">Account Number</label>
                  <input
                    type="text"
                    placeholder="Bank account number"
                    value={form.bank_account}
                    onChange={(e) => setForm({ ...form, bank_account: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white placeholder-white/40 focus:outline-none focus:border-primary/50"
                  />
                </div>
              </>
            )}

            {/* Notes */}
            <div>
              <label className="text-sm text-white/70 mb-1 block">Notes</label>
              <textarea
                placeholder="Optional notes..."
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white placeholder-white/40 focus:outline-none focus:border-primary/50 resize-none"
              />
            </div>

            {/* Aliases */}
            <div>
              <label className="text-sm text-white/70 mb-1 block">Aliases (Alternative Names)</label>
              <input
                type="text"
                placeholder="e.g., Mama, Mother (comma-separated)"
                value={form.aliases}
                onChange={(e) => setForm({ ...form, aliases: e.target.value })}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white placeholder-white/40 focus:outline-none focus:border-primary/50"
              />
              <p className="text-xs text-white/40 mt-1">Other names that should match this contact</p>
            </div>

            {/* Favorite Toggle */}
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_favorite}
                onChange={(e) => setForm({ ...form, is_favorite: e.target.checked })}
                className="sr-only"
              />
              <div className={cn(
                'w-10 h-6 rounded-full transition-colors relative',
                form.is_favorite ? 'bg-primary' : 'bg-white/20'
              )}>
                <div className={cn(
                  'absolute top-1 w-4 h-4 rounded-full bg-white transition-transform',
                  form.is_favorite ? 'translate-x-5' : 'translate-x-1'
                )} />
              </div>
              <span className="text-white/70">Add to favorites</span>
            </label>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-white/10 flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 rounded-lg border border-white/20 text-white/70 hover:bg-white/5 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 btn-primary flex items-center justify-center gap-2"
            >
              {saving ? (
                'Saving...'
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Save
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </>
  )
}
